// Simulated handheld RFID reader (ISO 11784/11785 FDX-B, 134.2 kHz) paired with the phone over Bluetooth.
// A production build would talk to the reader through Web Bluetooth / the reader's SDK; the UI flow is the same.
import { useEffect, useState } from "react";
import type { PetSummary } from "../../../shared/types";
import { api } from "../../api";
import { formatChip } from "./chip";

type ReaderState = "disconnected" | "connecting" | "ready" | "scanning";

const READER = { name: "VP-RFID 134", model: "FDX-B / HDX · ISO 11784/85", battery: 82 };
const randomChip = () => "900164" + Array.from({ length: 9 }, () => Math.floor(Math.random() * 10)).join("");

export function RfidScanner({ onChip }: { onChip: (chip: string) => void }) {
  const [state, setState] = useState<ReaderState>("disconnected");
  const [pets, setPets] = useState<PetSummary[]>([]);
  const [tag, setTag] = useState<string>("unknown");

  useEffect(() => { api.pets().then(setPets).catch(() => {}); }, []);

  useEffect(() => {
    if (state === "connecting") {
      const t = setTimeout(() => setState("ready"), 1400);
      return () => clearTimeout(t);
    }
    if (state === "scanning") {
      const t = setTimeout(() => {
        setState("ready");
        onChip(tag === "unknown" ? randomChip() : tag);
      }, 1800);
      return () => clearTimeout(t);
    }
  }, [state, tag, onChip]);

  return (
    <div className="rfid">
      <div className={`reader ${state}`}>
        <div className="reader-icon">
          <svg viewBox="0 0 64 64" width="56" height="56" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
            <rect x="22" y="10" width="20" height="44" rx="6" />
            <circle cx="32" cy="44" r="3" />
            <path d="M14 22a18 18 0 0 0 0 20M8 16a28 28 0 0 0 0 32M50 22a18 18 0 0 1 0 20M56 16a28 28 0 0 1 0 32" className="waves" />
          </svg>
        </div>
        {state === "scanning" && <><span className="ring" /><span className="ring r2" /><span className="ring r3" /></>}
      </div>

      <div className="reader-card">
        <div className="reader-row">
          <div>
            <div className="t1">{READER.name}</div>
            <div className="t3">{READER.model}</div>
          </div>
          <span className={`bt-pill ${state === "disconnected" ? "off" : state === "connecting" ? "pairing" : "on"}`}>
            {state === "disconnected" ? "Not connected" : state === "connecting" ? "Pairing…" : `● Connected · 🔋${READER.battery}%`}
          </span>
        </div>
        {state === "disconnected" && (
          <button className="btn teal big" onClick={() => setState("connecting")}>Connect reader (Bluetooth)</button>
        )}
        {state === "connecting" && <button className="btn big" disabled>Searching for reader…</button>}
        {(state === "ready" || state === "scanning") && (
          <>
            <div className="field">
              <label>Simulated tag under the reader</label>
              <select value={tag} onChange={(e) => setTag(e.target.value)} disabled={state === "scanning"}>
                <option value="unknown">Unregistered chip (new patient)</option>
                {pets.map((p) => <option key={p.id} value={p.microchip}>{p.displayName} · {formatChip(p.microchip)}</option>)}
              </select>
            </div>
            <button className="btn teal big" disabled={state === "scanning"} onClick={() => setState("scanning")}>
              {state === "scanning" ? "Reading 134.2 kHz…" : "Scan — hold reader over the neck / shoulder"}
            </button>
          </>
        )}
      </div>
      <div className="sim-badge">Simulation — no physical reader is connected</div>
    </div>
  );
}
